'use client'

import { useRef, useEffect, useState, useMemo, useCallback } from 'react'
import { keyBy } from 'lodash'
import { Chip, Text, Divider, Icon } from '@vinted/web-ui'
import { Filters16, X24 } from '@vinted/monochrome-icons'

import { useTrackAbTestCallback, useAbTest } from '@marketplace-web/shared/ab-tests'

import { reloadPage } from '@marketplace-web/shared/browser'
import { useTracking } from '@marketplace-web/shared/event-tracker'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { SeparatedList } from '@marketplace-web/shared/ui-helpers'
import { useLatestCallback } from '@marketplace-web/shared/ui-state'
import {
  addSelectedFilter,
  findFilterOptionById,
  removeSelectedFilter,
  removeSelectedFilters,
  DynamicFilterModel,
  ExperimentModel,
  SelectedDynamicFilterModel,
  PriceRange,
  DisplayType,
  DynamicExposeType,
  FilterModalNames,
  OptionType,
  SelectionType,
  SortByOption,
  Filter,
} from '@marketplace-web/domain/catalog-data'

import { clickEvent, clickFilterEvent } from '_libs/common/event-tracker/events'
import { viewFilterEvent } from '@marketplace-web/domain/catalog-filters-data'

import { ClickableElement } from 'constants/tracking/clickable-elements'
import { Screen } from 'constants/tracking/screens'

import { useCatalogContext } from '../../../CatalogProvider'
import { DynamicFilterReducerAction } from '../../../CatalogProvider/reducers/actionTypes'
import useFetchDynamicFiltersFacets from '../../../hooks/useFetchDynamicFiltersFacets'
import useFetchCatalogBrand from '../../../hooks/useFetchCatalogBrand'
import useFetchDynamicFilters from '../../../hooks/useFetchDynamicFilters'
import SortFilter from './SortFilter'
import CatalogFilter from './CatalogFilter'
import PriceFilter from './PriceFilter'
import CatalogFilterFacetedModal from '../Filters/CatalogFilterFaceted/CatalogFilterFacetedModal'
import ListFilterModal from '../common/DynamicFilters/ListFilter/ListFilterModal'
import SearchFilterModal from '../common/DynamicFilters/SearchFilter/SearchFilterModal'
import GridFilterModal from '../common/DynamicFilters/GridFilter/GridFilterModal'
import FilterModal from '../common/FilterModal'
import FilterModalNavigation from '../common/FilterModalNavigation'

type Props = {
  learnMoreUrl: string | null
}

type SharedFilterProps = {
  isFilterCellVisible: boolean
  onModalOpen: () => void
  onModalClose: () => void
}

const MobileFilters = ({ learnMoreUrl }: Props) => {
  const translate = useTranslate('catalog.filters')
  const { track } = useTracking()
  const fetchDynamicFilters = useFetchDynamicFilters()
  const fetchDynamicFiltersFacets = useFetchDynamicFiltersFacets()
  const fetchCatalogBrand = useFetchCatalogBrand()
  const {
    state: { configuration, dynamicFilters, data, filters, searchSession },
    dispatch,
  } = useCatalogContext()
  const trackAbTest = useTrackAbTestCallback()
  const currency = filters.currency || configuration.config.currency

  const holdoutQ4 = useAbTest('buyer_domain_holdout_2024q4')?.variant !== 'off'

  const isFacetedCategoriesEnabled =
    useAbTest('scoped_search_faceted_categories_v2')?.variant === 'on' && holdoutQ4

  const { globalSearchSessionId, globalCatalogBrowseSessionId } = searchSession
  const appliedCatalogsIds = filters.catalogIds
  const appliedSortOption = filters.sortBy
  const appliedPriceFrom = filters.priceFrom
  const appliedPriceTo = filters.priceTo
  const searchSessionId = searchSession.sessionId
  const dynamicFilterList = dynamicFilters.dynamicFilters
  const selectedDynamicFilterList = dynamicFilters.selectedDynamicFilters
  const temporarySelectedDynamicFilterList = dynamicFilters.temporarySelectedDynamicFilters
  const totalFacetCount = dynamicFilters.filterFacetsCount
  const allCatalogs = useMemo(() => {
    return keyBy(data.catalogs, 'id')
  }, [data.catalogs])

  const [modalName, setModalName] = useState<string>(FilterModalNames.Default)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [totalItemsCount, setTotalItemsCount] = useState<number | null>(0)
  const [isAnyFilterModalOpen, setIsAnyFilterModalOpen] = useState(false)
  const [selectedSortOption, setSelectedSortOption] = useState<SortByOption | null | undefined>(
    appliedSortOption,
  )

  const [selectedCatalogsIds, setSelectedCatalogsIds] = useState(appliedCatalogsIds)
  const [selectedPriceRange, setSelectedPriceRange] = useState({
    priceFrom: appliedPriceFrom,
    priceTo: appliedPriceTo,
  })

  const shownFilters = useRef<Array<string>>([])

  useEffect(() => {
    window.addEventListener('popstate', reloadPage)

    return () => {
      window.removeEventListener('popstate', reloadPage)
    }
  }, [])

  useEffect(() => {
    if (modalName !== FilterModalNames.Root) return

    dynamicFilterList.forEach(filter => {
      if (filter.isHidden && filter.experiment?.exposure_type === DynamicExposeType.View) {
        trackAbTest({
          id: filter.experiment.id,
          name: filter.experiment.name,
          variant: filter.experiment.variant,
        })
      }
    })
  }, [dynamicFilterList, modalName, trackAbTest])

  const clearSelectedFilters = () => {
    setSelectedSortOption(null)
    setSelectedCatalogsIds([])
    setSelectedPriceRange({ priceFrom: null, priceTo: null })

    fetchDynamicFilters({
      selectedDynamicFilters: [],
      selectedTemporaryCatalogIds: [],
      selectedTemporaryPriceRange: { priceFrom: null, priceTo: null },
      isTemporary: true,
    })

    setTotalItemsCount(null)
    dispatch({
      type: DynamicFilterReducerAction.SET_DYNAMIC_FILTER_FACETS,
      payload: { filterFacetsCount: null },
    })
  }

  const resetSelectedFilters = () => {
    setSelectedSortOption(appliedSortOption)
    setSelectedCatalogsIds(appliedCatalogsIds)
    setSelectedPriceRange({
      priceFrom: appliedPriceFrom,
      priceTo: appliedPriceTo,
    })

    fetchDynamicFilters({
      selectedDynamicFilters: [],
      selectedTemporaryCatalogIds: appliedCatalogsIds,
      isTemporary: true,
    })
  }

  const updateTemporarySelectedFilters = () => {
    fetchDynamicFilters({
      selectedDynamicFilters: selectedDynamicFilterList,
      selectedTemporaryCatalogIds: appliedCatalogsIds,
      isTemporary: true,
    })
  }

  const openModal = () => {
    setModalName(FilterModalNames.Root)
    setIsModalOpen(true)

    updateTemporarySelectedFilters()
  }

  const closeModal = () => {
    setModalName(FilterModalNames.Default)
    setIsModalOpen(false)

    shownFilters.current = []
  }

  const trackCancelClick = () => {
    track(
      clickEvent({
        target: ClickableElement.CancelFilters,
        screen: Screen.MobileCatalogFilters,
      }),
    )
  }

  const handleSubmit = () => {
    closeModal()

    fetchDynamicFilters({
      selectedDynamicFilters: temporarySelectedDynamicFilterList,
      selectedFilters: {
        ...selectedPriceRange,
        sortBy: selectedSortOption,
        catalogIds: selectedCatalogsIds,
        currency,
      },
    })
    fetchCatalogBrand({ selectedDynamicFilters: temporarySelectedDynamicFilterList })

    dynamicFilterList.forEach(filter => {
      if (filter.experiment?.exposure_type !== DynamicExposeType.Apply) return

      const selectedFilter = temporarySelectedDynamicFilterList.find(
        item => item.type === filter.type,
      )

      if (!selectedFilter?.ids?.length) return

      trackAbTest({
        id: filter.experiment.id,
        name: filter.experiment.name,
        variant: filter.experiment.variant,
      })
    })
  }

  const handleSubmitWithTracking = () => {
    handleSubmit()

    track(
      clickEvent({
        target: ClickableElement.ApplyFilters,
        screen: Screen.MobileCatalogFilters,
      }),
    )
  }

  const handleItemFacetChange = useCallback(
    (currentSelectedFilters: Array<SelectedDynamicFilterModel>, type: string) => {
      let newTotal = 0

      const filteredSelections = currentSelectedFilters.filter(
        selectedFilter => selectedFilter.type === type,
      )

      if (!filteredSelections.length) {
        dispatch({
          type: DynamicFilterReducerAction.SET_DYNAMIC_FILTER_FACETS,
          payload: { filterFacetsCount: null },
        })
        setTotalItemsCount(newTotal)

        return
      }

      filteredSelections.forEach(selectedFilter => {
        const matchingFilter = dynamicFilterList.find(filter => filter.type === selectedFilter.type)

        if (!matchingFilter || !selectedFilter.ids.length) return

        if (matchingFilter.isAsyncFacet) {
          fetchDynamicFiltersFacets({
            selectedDynamicFilters: currentSelectedFilters,
            selectedTemporaryCatalogIds: selectedCatalogsIds,
            selectedTemporaryPriceRange: selectedPriceRange,
          })
        } else {
          selectedFilter.ids.forEach(id => {
            const matchedOption = findFilterOptionById(matchingFilter.options, id)
            const itemCount = matchedOption?.itemsCount ?? 0
            newTotal += itemCount
          })
        }
      })

      setTotalItemsCount(newTotal)
    },
    [
      dispatch,
      fetchDynamicFiltersFacets,
      dynamicFilterList,
      selectedCatalogsIds,
      selectedPriceRange,
    ],
  )

  const handleFilterClick = (filter: Filter, type?: string, experiment?: ExperimentModel) => () => {
    setModalName(`${FilterModalNames.Filter}_${filter}${type ? `_${type}` : ''}`)

    if (type) {
      handleItemFacetChange(temporarySelectedDynamicFilterList, type)
    }

    track(
      clickFilterEvent({
        filter,
        screen: Screen.MobileCatalogFilters,
        searchSessionId,
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
        type,
        code: type,
      }),
    )

    if (experiment?.exposure_type === DynamicExposeType.Click) {
      trackAbTest({
        id: experiment.id,
        name: experiment.name,
        variant: experiment.variant,
      })
    }
  }

  const trackClearFiltersClick = () => {
    track(
      clickEvent({
        target: ClickableElement.ClearFilters,
        screen: Screen.MobileCatalogFilters,
      }),
    )
  }

  const trackFilterView = (filter: Filter, type?: string, experiment?: ExperimentModel) => {
    track(
      viewFilterEvent({
        filter,
        screen: Screen.MobileCatalogFilters,
        searchSessionId,
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
        type,
        code: type,
      }),
    )

    if (experiment?.exposure_type === DynamicExposeType.View) {
      trackAbTest({
        id: experiment.id,
        name: experiment.name,
        variant: experiment.variant,
      })
    }
  }

  const trackFilterCellClick = () => {
    track(
      clickEvent({
        target: ClickableElement.SelectFilters,
      }),
    )
  }

  const handleFilterCellView =
    (filter: Filter, type?: string, experiment?: ExperimentModel) => () => {
      const shownFilter = type || filter

      if (shownFilters.current.includes(shownFilter)) return

      shownFilters.current.push(shownFilter)

      trackFilterView(filter, type, experiment)
    }

  const handleFilterCellClick = () => {
    openModal()
    trackFilterCellClick()
  }

  const handlePrefixClick = () => {
    closeModal()
    resetSelectedFilters()
    trackCancelClick()
  }

  const handleSuffixClick = () => {
    clearSelectedFilters()
    trackClearFiltersClick()
  }

  const handleCatalogSelect = (newCatalogIds: Array<number>) => {
    setSelectedCatalogsIds(newCatalogIds)

    fetchDynamicFilters({
      selectedDynamicFilters: selectedDynamicFilterList,
      selectedTemporaryCatalogIds: newCatalogIds,
      isTemporary: true,
    })
  }

  const handlePriceRange = (range: PriceRange) => {
    setSelectedPriceRange(range)

    fetchDynamicFilters({
      selectedDynamicFilters: temporarySelectedDynamicFilterList,
      selectedTemporaryPriceRange: range,
      selectedFilters: { currency },
      isTemporary: true,
    })
  }

  const handleFilterUpdate = useLatestCallback(
    (updatedSelectedFilters: Array<SelectedDynamicFilterModel>, type: string) => {
      fetchDynamicFilters({ selectedDynamicFilters: updatedSelectedFilters, isTemporary: true })
      handleItemFacetChange(updatedSelectedFilters, type)
    },
  )

  const handleAddSelectedFilter = useLatestCallback(
    (newFilter: { type: string; ids: Array<number> }) => {
      const updatedSelectedFilters = addSelectedFilter(
        newFilter,
        temporarySelectedDynamicFilterList,
      )

      handleFilterUpdate(updatedSelectedFilters, newFilter.type)
    },
  )

  const handleRemoveSelectedFilter = useLatestCallback(
    (removeFilter: { type: string; id: number }) => {
      const updatedSelectedFilters = removeSelectedFilter(
        removeFilter,
        temporarySelectedDynamicFilterList,
      )

      handleFilterUpdate(updatedSelectedFilters, removeFilter.type)
    },
  )

  const handleRemoveSelectedFilters = useLatestCallback(
    (removeFilters: { type: string; ids?: Array<number> }) => {
      const updatedSelectedFilters = removeSelectedFilters(
        removeFilters,
        temporarySelectedDynamicFilterList,
      )

      handleFilterUpdate(updatedSelectedFilters, removeFilters.type)
    },
  )

  const handleDynamicFilterToggle = useLatestCallback(
    (id: number, type: string, selectionType: SelectionType, selectedIds: Array<number>) => {
      if (!selectedIds.includes(id)) {
        const newIds = selectionType === SelectionType.Single ? [id] : [...selectedIds, id]
        handleAddSelectedFilter({ type, ids: newIds })
      } else if (selectionType === SelectionType.Multi) {
        handleRemoveSelectedFilter({ type, id })
      }
    },
  )

  const clearDynamicFilterOptions = (type: string, ids?: Array<number>) => {
    handleRemoveSelectedFilters({ type, ids })
  }

  const handleAnyFilterModalOpen = () => {
    setIsAnyFilterModalOpen(true)
  }

  const handleAnyFilterModalClose = () => {
    setModalName(FilterModalNames.Root)
    shownFilters.current = []
    setIsAnyFilterModalOpen(false)
  }

  const getTemporarySelectedFilter = (type: string) =>
    temporarySelectedDynamicFilterList.find(filter => filter.type === type)

  const getRootOptionType = (dynamicFilter: DynamicFilterModel) =>
    dynamicFilter.options.find(option => !!option.type)?.type || OptionType.Default

  const renderModalNavigation = () => (
    <FilterModalNavigation
      title={translate('mobile.filters_screen.title')}
      icon={<Icon name={X24} />}
      prefixAriaLabel={translate('a11y.actions.close')}
      suffixText={translate('mobile.filters_screen.actions.clear_filters')}
      testId="modal-navigation"
      onPrefixClick={handlePrefixClick}
      onSuffixClick={handleSuffixClick}
    />
  )

  const renderDynamicFilter = (filter: DynamicFilterModel, sharedProps: SharedFilterProps) => {
    const temporarySelectedDynamicFilter = getTemporarySelectedFilter(filter.type)
    const key = `${filter.type}-${filter.id}`
    let totalFacetsCount: number | null | undefined
    if (filter.isAsyncFacet) {
      totalFacetsCount = totalFacetCount ?? undefined
    } else {
      totalFacetsCount = totalItemsCount === 0 ? null : totalItemsCount
    }

    switch (filter.displayType) {
      case DisplayType.List: {
        return (
          <ListFilterModal
            key={key}
            type={filter.type}
            title={filter.title}
            selectionType={filter.selectionType}
            isSelectionHighlighted={filter.isSelectionHighlighted}
            isNewFilter={filter.isNewFilter}
            options={filter.options}
            fromScreen={Screen.MobileCatalogFilters}
            isHidden={filter.isHidden}
            links={filter.links}
            totalItemsCount={totalFacetsCount}
            rootOptionType={getRootOptionType(filter)}
            onSubmit={handleSubmit}
            onFilterCellClick={handleFilterClick(Filter.Dynamic, filter.type, filter.experiment)}
            onFilterCellView={handleFilterCellView(Filter.Dynamic, filter.type, filter.experiment)}
            selectedIds={temporarySelectedDynamicFilter?.ids || []}
            clearSelectedOptions={clearDynamicFilterOptions}
            onListOptionSelect={id => {
              handleDynamicFilterToggle(
                id,
                filter.type,
                filter.selectionType,
                temporarySelectedDynamicFilter?.ids || [],
              )
            }}
            {...sharedProps}
          />
        )
      }
      case DisplayType.ListSearch: {
        return (
          <SearchFilterModal
            key={key}
            type={filter.type}
            title={filter.title}
            selectionType={filter.selectionType}
            isSelectionHighlighted={filter.isSelectionHighlighted}
            options={filter.options}
            fromScreen={Screen.MobileCatalogFilters}
            translations={filter.searchTranslations}
            isHidden={filter.isHidden}
            links={filter.links}
            totalItemsCount={totalFacetsCount}
            rootOptionType={getRootOptionType(filter)}
            onSubmit={handleSubmit}
            onFilterCellClick={handleFilterClick(Filter.Dynamic, filter.type, filter.experiment)}
            onFilterCellView={handleFilterCellView(Filter.Dynamic, filter.type, filter.experiment)}
            selectedIds={temporarySelectedDynamicFilter?.ids || []}
            clearSelectedOptions={clearDynamicFilterOptions}
            onListOptionSelect={id => {
              handleDynamicFilterToggle(
                id,
                filter.type,
                filter.selectionType,
                temporarySelectedDynamicFilter?.ids || [],
              )
            }}
            {...sharedProps}
          />
        )
      }
      case DisplayType.Grid: {
        return (
          <GridFilterModal
            key={key}
            type={filter.type}
            title={filter.title}
            isSelectionHighlighted={filter.isSelectionHighlighted}
            isNewFilter={filter.isNewFilter}
            options={filter.options}
            isHidden={filter.isHidden}
            fromScreen={Screen.MobileCatalogFilters}
            totalItemsCount={totalFacetsCount}
            rootOptionType={getRootOptionType(filter)}
            onSubmit={handleSubmit}
            onFilterCellClick={handleFilterClick(Filter.Dynamic, filter.type, filter.experiment)}
            onFilterCellView={handleFilterCellView(Filter.Dynamic, filter.type, filter.experiment)}
            selectedIds={temporarySelectedDynamicFilter?.ids || []}
            clearSelectedOptions={clearDynamicFilterOptions}
            onListOptionSelect={id => {
              handleDynamicFilterToggle(
                id,
                filter.type,
                filter.selectionType,
                temporarySelectedDynamicFilter?.ids || [],
              )
            }}
            {...sharedProps}
          />
        )
      }
      case DisplayType.HybridPrice: {
        return (
          <PriceFilter
            key={key}
            selectedPriceRange={selectedPriceRange}
            minimumValidation={filter.minimumValidation}
            onPriceRangeChange={handlePriceRange}
            onSubmit={handleSubmitWithTracking}
            onFilterCellClick={handleFilterClick(Filter.Price)}
            onFilterCellView={handleFilterCellView(Filter.Price)}
            {...sharedProps}
          />
        )
      }
      default:
        return null
    }
  }

  const renderDynamicFilters = (sharedProps: SharedFilterProps) => {
    return dynamicFilterList.map(filter => renderDynamicFilter(filter, sharedProps))
  }

  const renderFilters = () => {
    const sharedProps = {
      isFilterCellVisible: !isAnyFilterModalOpen,
      onModalOpen: handleAnyFilterModalOpen,
      onModalClose: handleAnyFilterModalClose,
    }

    return (
      <SeparatedList separator={<Divider />}>
        {isFacetedCategoriesEnabled ? (
          <CatalogFilterFacetedModal
            catalogIds={selectedCatalogsIds}
            onCatalogSelect={handleCatalogSelect}
            onFilterCellClick={handleFilterClick(Filter.Category)}
            onFilterCellView={handleFilterCellView(Filter.Category)}
            {...sharedProps}
          />
        ) : (
          <CatalogFilter
            allCatalogs={allCatalogs}
            selectedCatalogsIds={selectedCatalogsIds}
            onCatalogSelect={handleCatalogSelect}
            onSubmit={handleSubmitWithTracking}
            onFilterCellClick={handleFilterClick(Filter.Category)}
            onFilterCellView={handleFilterCellView(Filter.Category)}
            {...sharedProps}
          />
        )}
        {renderDynamicFilters(sharedProps)}
        <SortFilter
          learnMoreUrl={learnMoreUrl}
          selectedOption={selectedSortOption}
          onOptionClick={setSelectedSortOption}
          onSubmit={handleSubmitWithTracking}
          onFilterCellClick={handleFilterClick(Filter.Sort)}
          onFilterCellView={handleFilterCellView(Filter.Sort)}
          {...sharedProps}
        />
      </SeparatedList>
    )
  }

  const getAppliedFiltersCount = () => {
    let count = 0

    if (appliedCatalogsIds.length) count += 1
    if (appliedSortOption && appliedSortOption !== SortByOption.Relevance) count += 1
    if (appliedPriceFrom || appliedPriceTo) count += 1

    selectedDynamicFilterList.forEach(filter => {
      if (filter?.ids.length) count += 1
    })

    return count
  }

  const appliedFiltersCount = getAppliedFiltersCount()

  return (
    <>
      <Chip
        prefix={<Icon name={Filters16} />}
        radius={Chip.Radius.Round}
        text={translate('mobile.filters_chip_text', {
          count: appliedFiltersCount ? `(${appliedFiltersCount})` : null,
        })}
        textType={Text.Type.Subtitle}
        activated={!!appliedFiltersCount}
        onClick={handleFilterCellClick}
        testId="trigger"
      />
      <FilterModal
        isOpen={isModalOpen}
        testId="modal"
        renderNavigation={renderModalNavigation}
        onSubmit={handleSubmitWithTracking}
      >
        {renderFilters()}
        <Divider />
      </FilterModal>
    </>
  )
}

export default MobileFilters
